import { useTheme } from '@mui/material';
import { Button } from '@portals/core/src/components/Button/Button';
import {
  Card as CardUI,
  CardActions,
  CardContent,
  CardHeadlineHeader,
  CardProps,
} from '@portals/core/src/components/Card/Card';
import { ProductLink } from '@portals/core/src/components/ProductLink/ProductLink';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import type { CustomVariant as TypographyVariants } from '@portals/core/src/components/Typography/Typography.types.d';
import type { Theme } from '@portals/core/src/themes/themes';
import { ArrowRight } from '@portals/icons/src/ArrowRight/ArrowRight';
import React from 'react';

import { ServiceCard as CardModel } from '../../../models';
import { useLinkProps } from '../../hooks/useLinkProps';
import { AspectMedia, MEDIA_SIZES as ASPECT_MEDIA_SIZES } from '../AspectMedia/AspectMedia';
import { TopInfoBox } from '../TopInfoBox/TopInfoBox';

export type ServiceCardVariants = Extract<
  CardProps['variant'],
  | 'service:heroMediaLeft'
  | 'service:heroMediaRight'
  | 'service:heroMediaSquare'
  | 'service:cardHalf'
  | 'service:campaign'
  | 'service:list'
>;

export interface Props {
  node: CardModel;
  variant?: ServiceCardVariants;
}

const REQUIRES_16x9_MEDIA: { [key in ServiceCardVariants]-?: boolean | { xs: string; lg: string } } = {
  'service:heroMediaLeft': true,
  'service:heroMediaRight': true,
  'service:heroMediaSquare': false,
  'service:cardHalf': true,
  'service:campaign': false,
  'service:list': true,
};

const REQUIRES_1x1_MEDIA: { [key in ServiceCardVariants]-?: boolean | { xs: string; lg: string } } = {
  'service:heroMediaLeft': false,
  'service:heroMediaRight': false,
  'service:heroMediaSquare': true,
  'service:cardHalf': false,
  'service:campaign': true,
  'service:list': false,
};

const HEADLINE_TYPOGRAPHY_VARIANT: { [key in ServiceCardVariants]-?: TypographyVariants } = {
  'service:heroMediaLeft': 'h3',
  'service:heroMediaRight': 'h3',
  'service:heroMediaSquare': 'h2',
  'service:cardHalf': 'h3',
  'service:campaign': 'h2',
  'service:list': 'h3',
};

const MEDIA_SIZES: { [key in ServiceCardVariants]-?: string } = {
  'service:heroMediaLeft': ASPECT_MEDIA_SIZES.lg,
  'service:heroMediaRight': ASPECT_MEDIA_SIZES.lg,
  'service:heroMediaSquare': ASPECT_MEDIA_SIZES.sm,
  'service:cardHalf': ASPECT_MEDIA_SIZES.md,
  'service:campaign': ASPECT_MEDIA_SIZES.md,
  'service:list': ASPECT_MEDIA_SIZES.md,
};

export const ServiceCard: React.FunctionComponent<Props> = ({ node, variant = 'service:campaign' }: Props) => {
  const { linkProps, anchorProps } = useLinkProps(node.link ? node.link[0] : null);
  const theme = useTheme<Theme>();

  const display_16x9 = REQUIRES_16x9_MEDIA[variant];
  const display_1x1 = REQUIRES_1x1_MEDIA[variant];

  return (
    <CardUI id={`${node.__contentType}.${node.id}`} variant={variant} {...linkProps}>
      {node.image?.media_16x9 && display_16x9 ? (
        <AspectMedia
          media={node.image.media_16x9}
          aspectWidth={16}
          aspectHeight={9}
          mediaSizes={MEDIA_SIZES[variant]}
          sx={typeof display_16x9 === 'object' ? { display: display_16x9 } : {}}
        />
      ) : null}

      {node.image?.media_1x1 && display_1x1 ? (
        <AspectMedia
          media={node.image.media_1x1}
          aspectWidth={1}
          aspectHeight={1}
          mediaSizes={MEDIA_SIZES[variant]}
          sx={typeof display_1x1 === 'object' ? { display: display_1x1 } : {}}
        />
      ) : null}

      <CardHeadlineHeader>
        {node.headline ? (
          <Typography
            color={theme.palette.text.primary}
            variant={HEADLINE_TYPOGRAPHY_VARIANT[variant]}
            {...anchorProps}
            innerPadding="both"
          >
            {node.headline.text}
          </Typography>
        ) : null}
      </CardHeadlineHeader>

      <CardContent>
        {node.text && (
          <Typography variant="body2" color="text.secondary" innerPadding="both">
            {node.text}
          </Typography>
        )}
        {node.benefits && <TopInfoBox node={{ ...node.benefits, size: 'small' }} />}
      </CardContent>

      <CardActions>
        {(variant === 'service:heroMediaLeft' || variant === 'service:heroMediaRight') && node.link?.length ? (
          <Button endIcon={<ArrowRight />} component="a" {...linkProps}>
            {node.link[0].text}
          </Button>
        ) : node.link?.length ? (
          <ProductLink {...linkProps}>{node.link[0].text}</ProductLink>
        ) : null}
      </CardActions>
    </CardUI>
  );
};
